import axiosClient, { app_uri } from "../../../utils/axiosFactory";
import { AnalyticsClient } from "../../repsaApi";
import {
  IBusinessPlanningDataModel,
  BusinessPlanningCategoryType,
  IBusinessZonesResponse,
  IBusinessZonesSettings,
  ILeadToClosedResponse,
} from "../../../types/types";

const client = new AnalyticsClient(app_uri, axiosClient());

/**
 * Get business planning data from the API
 */
export async function getBusinessPlanningData(
  yearIds: number[] | undefined,
  forecastYear: "thisYear" | "nextYear",
  categories: BusinessPlanningCategoryType[],
  onlySb: boolean = true,
  coAgentIds?: number[],
): Promise<IBusinessPlanningDataModel> {
  const axios = axiosClient();
  let url = `${app_uri}/api/v1/Analytics/business-planning-data?`;

  if (yearIds && yearIds.length > 0) {
    yearIds.forEach((id) => {
      url += `YearIds=${encodeURIComponent(id)}&`;
    });
  }

  url += `ForecastYear=${encodeURIComponent(forecastYear)}&`;

  categories.forEach((cat) => {
    url += `Categories=${encodeURIComponent(cat)}&`;
  });

  url += `OnlySb=${encodeURIComponent(onlySb)}&`;

  if (coAgentIds && coAgentIds.length > 0) {
    url += `CoAgentIds=${encodeURIComponent(coAgentIds.join(","))}&`;
  }

  url = url.replace(/[?&]$/, "");

  const response = await axios.get<IBusinessPlanningDataModel>(url);
  return response.data;
}

/**
 * Get business zones data from the API
 */
export async function getBusinessZonesData(
  yearIds: number[] | undefined,
  greenThreshold: number,
  yellowThreshold: number,
  zoneMetric: string,
  coAgentIds?: number[],
): Promise<IBusinessZonesResponse> {
  const axios = axiosClient();
  let url = `${app_uri}/api/v1/Analytics/business-zones?`;

  if (yearIds && yearIds.length > 0) {
    yearIds.forEach((id) => {
      url += `YearIds=${encodeURIComponent(id)}&`;
    });
  }

  url += `GreenThreshold=${encodeURIComponent(greenThreshold)}&`;
  url += `YellowThreshold=${encodeURIComponent(yellowThreshold)}&`;
  url += `ZoneMetric=${encodeURIComponent(zoneMetric)}&`;

  if (coAgentIds && coAgentIds.length > 0) {
    url += `CoAgentIds=${encodeURIComponent(coAgentIds.join(","))}&`;
  }

  url = url.replace(/[?&]$/, "");

  const response = await axios.get<IBusinessZonesResponse>(url);
  return response.data;
}

/**
 * Get business zones settings from the API
 */
export async function getBusinessZonesSettings(): Promise<IBusinessZonesSettings | null> {
  const axios = axiosClient();
  const url = `${app_uri}/api/v1/Analytics/business-zones-settings`;

  try {
    const response = await axios.get<IBusinessZonesSettings>(url);
    return response.data;
  } catch (error: any) {
    if (error.response?.status === 404) {
      return null; // No settings found
    }
    throw error;
  }
}

/**
 * Save business zones settings to the API
 */
export async function saveBusinessZonesSettings(
  settings: IBusinessZonesSettings,
): Promise<void> {
  const axios = axiosClient();
  const url = `${app_uri}/api/v1/Analytics/business-zones-settings`;

  await axios.post(url, settings);
}

/**
 * Get financial category data summary from the API
 */
export async function getFinancialCategoryDataSummaryWithFilter(
  categoryId: string,
  yearIds: number[] | undefined,
  coAgentIds?: number[],
): Promise<any> {
  const axios = axiosClient();
  let url = `${app_uri}/api/v1/Analytics/financial-category-data-summary?`;

  url += `CategoryId=${encodeURIComponent(categoryId)}&`;

  if (yearIds && yearIds.length > 0) {
    yearIds.forEach((id) => {
      url += `YearIds=${encodeURIComponent(id)}&`;
    });
  }

  if (coAgentIds && coAgentIds.length > 0) {
    url += `CoAgentIds=${encodeURIComponent(coAgentIds.join(","))}&`;
  }

  url = url.replace(/[?&]$/, "");

  const response = await axios.get(url);
  return response.data;
}

/**
 * Get category data summary from the API. With includeUnderContracts the Under Contract
 * transactions of every year folder come back too, whatever yearIds holds.
 */
export async function getCategoryDataSummaryWithFilter(
  categoryId: string,
  yearIds: number[] | undefined,
  transactionTypes: number[] | undefined,
  coAgentIds?: number[],
  includeUnderContracts: boolean = false,
): Promise<any> {
  const axios = axiosClient();
  let url = `${app_uri}/api/v1/Analytics/category-data-summary?`;

  url += `CategoryId=${encodeURIComponent(categoryId)}&`;

  if (yearIds && yearIds.length > 0) {
    yearIds.forEach((id) => {
      url += `YearsIds=${encodeURIComponent(id)}&`;
    });
  }

  if (transactionTypes && transactionTypes.length > 0) {
    transactionTypes.forEach((id) => {
      url += `TransactionTypes=${encodeURIComponent(id)}&`;
    });
  }

  if (coAgentIds && coAgentIds.length > 0) {
    url += `CoAgentIds=${encodeURIComponent(coAgentIds.join(","))}&`;
  }

  if (includeUnderContracts) {
    url += `IncludeUnderContracts=true&`;
  }

  url = url.replace(/[?&]$/, "");

  const response = await axios.get(url);
  return response.data;
}

/**
 * Get the Lead-to-Closed time distribution (Transactions only)
 */
export async function getLeadToClosed(
  yearIds: number[] | undefined,
  transactionTypes: number[] | undefined,
  coAgentIds?: number[],
): Promise<ILeadToClosedResponse> {
  const axios = axiosClient();
  let url = `${app_uri}/api/v1/Analytics/lead-to-closed?`;

  if (yearIds && yearIds.length > 0) {
    yearIds.forEach((id) => {
      url += `YearIds=${encodeURIComponent(id)}&`;
    });
  }

  if (transactionTypes && transactionTypes.length > 0) {
    transactionTypes.forEach((id) => {
      url += `TransactionTypes=${encodeURIComponent(id)}&`;
    });
  }

  if (coAgentIds && coAgentIds.length > 0) {
    url += `CoAgentIds=${encodeURIComponent(coAgentIds.join(","))}&`;
  }

  url = url.replace(/[?&]$/, "");

  const response = await axios.get<ILeadToClosedResponse>(url);
  return response.data;
}

export default client;
